<script setup>
import { defineProps, onMounted, ref } from 'vue'
import axios from 'axios'
import VueJsonPretty from 'vue-json-pretty'
import 'vue-json-pretty/lib/styles.css'

const API_URL = process.env.VUE_APP_API_URL

const emit = defineEmits(['slideover-close'])

const is_running = ref(false)
const failed = ref(false)
const respone_obj = ref(null)

const props = defineProps({
  query: { type: String },
  method: { type: String },
  name: { type: String },
  body: { type: Object }
})

async function get() {
  try {
    let { data } = await axios.get(API_URL + props.query)
    respone_obj.value = data
  } catch {
    failed.value = true
  }
}

async function post() {
  try {
    let { data } = await axios.post(API_URL + props.query, props.body)
    respone_obj.value = data
  } catch {
    failed.value = true
  }
}

onMounted(async () => {
  is_running.value = true

  if (props.method === 'get' || props.method === 'GET') {
    await get()
  } else if (props.method === 'post' || props.method === 'POST') {
    await post()
  } else {
    failed.value = true
  }

  is_running.value = false
})
</script>

<template>
  <div class="api-slideover">
    <!-- Header -->
    <div class="box-header">
      <h3>
        <span v-if="props.method === 'GET' || props.method === 'get'" class="badge rounded-pill text-bg-success">GET</span>
        <span v-else-if="props.method === 'POST' || props.method === 'post'" class="badge rounded-pill text-bg-primary">POST</span>
        <span v-else class="badge rounded-pill text-bg-secondary">{{ props.method }}</span>
        {{ props.name }}
      </h3>
    </div>

    <!-- Content -->
    <div class="box-content">
      <!-- Loading State -->
      <div v-if="is_running" class="state-container">
        <div class="state-icon">
          <font-awesome-icon style="font-size: 5rem;" :icon="['fas', 'gear']" spin />
        </div>
        <h3 class="state-title">Sending...</h3>
        <p class="state-message">Your response will be ready in a second :)</p>
      </div>

      <!-- Error State -->
      <div v-else-if="failed" class="state-container">
        <div class="state-icon error">
          <font-awesome-icon style="font-size: 5rem; color: #ca5050;" :icon="['fas', 'circle-exclamation']"/>
        </div>
        <h3 class="state-title">Failed</h3>
        <p class="state-message">Sorry! Something went wrong. Ensure that the request parameters are correct.</p>
      </div>

      <!-- Success State -->
      <div v-else class="content-sections">
        <!-- Request URL -->
        <div class="menu-container mb-3">
          <h5 class="section-title">REQUEST</h5>
          <div class="box padded">
            <code class="request-url">{{ API_URL + props.query }}</code>
          </div>
        </div>

        <!-- Request Body (POST only) -->
        <div v-if="props.body && (props.method === 'POST' || props.method === 'post')" class="menu-container mb-3">
          <h5 class="section-title">REQUEST BODY</h5>
          <div class="box padded">
            <vue-json-pretty :data="props.body" style="overflow: auto; max-height: 400px;"/>
          </div>
        </div>

        <!-- Response -->
        <div class="menu-container">
          <h5 class="section-title">RESPONSE</h5>
          <div class="box padded">
            <vue-json-pretty :data="respone_obj" style="overflow: auto; max-height: 600px;"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.api-slideover {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  color: white;
  margin: 0;
  padding: 0;
}

.box-header {
  background-color: rgba(255, 255, 255, 0.03);
  padding: 1rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  width: 100%;
  margin: 0;
  border-radius: 10px 0 0 0;
}

.box-header h3 {
  margin: 0;
  color: #E5E7EB;
  font-size: 1.1rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
}

.box-content {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
}

/* State Containers (Loading/Error) */
.state-container {
  text-align: center;
  padding: 3rem 1rem;
}

.state-icon {
  margin-bottom: 1.5rem;
  color: #5094ed;
}

.state-icon.error {
  color: #ca5050;
}

.state-title {
  color: white;
  margin-bottom: 0.75rem;
  font-size: 1.5rem;
}

.state-message {
  color: #9CA3AF;
  margin: 0;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

/* Content Sections */
.content-sections {
  width: 100%;
}

.menu-container {
  margin-bottom: 1.5rem;
}

.section-title {
  color: #E5E7EB;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  letter-spacing: 0.05em;
}

.box {
  background-color: #171C24;
  border: 1px solid #374151;
  border-radius: 10px;
}

.box.padded {
  padding: 1rem;
}

.request-url {
  color: #E5E7EB;
  word-break: break-all;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
}

/* Custom scrollbar */
.box-content::-webkit-scrollbar {
  width: 6px;
}

.box-content::-webkit-scrollbar-track {
  background: #1f2937;
}

.box-content::-webkit-scrollbar-thumb {
  background: #374151;
  border-radius: 3px;
}

.box-content::-webkit-scrollbar-thumb:hover {
  background: #4b5563;
}
</style>
